import { Currency } from '@/types/currency';
import { PurchaseList, PurchaseListItem } from '@/types/entity/purchase-list';
import { LineItem } from '@shared/types/wishlist/LineItem';
import { Wishlist } from '@shared/types/wishlist/Wishlist';
import {
  isRemovedFromSaleForLocale,
  isStockDueSoonEligibleFromCodes,
  parseCtLifecycleCodes,
} from '@/utils/product/lifecycle';
import { mapMasterAttributes } from './map-master-attributes';
import { mapStore } from './map-store';
import { normalizeBrandName } from './normalize-brand-name';

export const mapPurchaseItem = (
  { lineItemId, name, count, variant, _url }: LineItem,
  locale?: string,
): PurchaseListItem => {
  const fractionDigits = variant?.price?.fractionDigits ?? 2;
  const divisor = Math.pow(10, fractionDigits);
  const discountedCents = variant?.discountedPrice?.value?.centAmount;
  // BFF re-keys every CT attribute as `variants.attributes.<name>` — same as map-product.ts
  const variantAttributes = (variant?.attributes ?? {}) as Record<string, unknown>;
  const getAttr = (key: string): unknown => variantAttributes[`variants.attributes.${key}`];
  // EWM-327 — `productBrand` is a plain string, a JSON envelope
  // (`{"brandInfo":{"name":...}}`) or empty; normalize to the display name.
  const brand = normalizeBrandName(getAttr('productBrand'));
  const mpnRaw = getAttr('mpn');
  // Derive on-special badge from discounted price — same logic as map-product.ts (EWM-194)
  const badge = typeof discountedCents === 'number' ? ('on-special' as const) : undefined;
  // EWM-756 — mirror map-lineitem.ts: derive the "Stock due soon" eligibility
  // from the same CT `productLifeCycle` source so an out-of-stock N/P item shows
  // "Stock due soon" instead of "Out of stock" in the disabled-button tooltip.
  const isStockDueSoonEligible = isStockDueSoonEligibleFromCodes(parseCtLifecycleCodes(variantAttributes));
  // EWM-1090 — availability flags for the shared add-to-cart gate
  // (`resolveAddToCartAvailability`, EWM-360), so the purchase-list row + "Add
  // All" derive the SAME rule as the PDP/PLP/uploader. `removedFromSale` is the
  // region-scoped `GO` code (per-country: pulled in NZ can stay on sale in AU),
  // mirroring `map-lineitem`/`map-product`; `priceUnavailable` is an absent
  // variant price. Both hard-disable Add-to-Cart ahead of the back-order rescue.
  const removedFromSale = isRemovedFromSaleForLocale(variantAttributes, locale);
  const priceUnavailable = typeof variant?.price?.centAmount !== 'number';
  return {
    id: lineItemId,
    sku: variant?.sku ?? '',
    url: _url ?? '#',
    name: name ?? '',
    quantity: count ?? 1,
    image: variant?.images?.[0],
    price: (variant?.price?.centAmount ?? 0) / divisor,
    ...(typeof discountedCents === 'number' ? { discountedPrice: discountedCents / divisor } : {}),
    ...(brand ? { brand } : {}),
    ...(typeof mpnRaw === 'string' && mpnRaw.length > 0 ? { mpn: mpnRaw } : {}),
    ...(badge ? { badge } : {}),
    currency: (variant?.price?.currencyCode ?? 'AUD') as Currency,
    inStock: !!variant?.isOnStock,
    ...(isStockDueSoonEligible ? { isStockDueSoonEligible } : {}),
    removedFromSale,
    priceUnavailable,
    specifications: variant ? mapMasterAttributes(variant) : [],
  };
};

export const mapPurchaseList = (
  { wishlistId, name, description, lineItems, store, businessUnitKey, account, createdAt }: Wishlist,
  defaultPurchaseListId?: string,
  // EWM-1090 — drives the region-scoped `removedFromSale` (GO) flag; defaults to
  // en-AU (matching `map-product`) for callers that don't surface a locale.
  locale?: string,
): PurchaseList => {
  return {
    id: wishlistId as string,
    name: name ?? '',
    store: store ? mapStore(store) : undefined,
    description: description ?? '',
    items: (lineItems ?? []).map((lineItem) => mapPurchaseItem(lineItem, locale)),
    businessUnitKey: businessUnitKey ?? '',
    account: account,
    createdAt: createdAt ?? '',
    ...(defaultPurchaseListId ? { isDefault: wishlistId === defaultPurchaseListId } : {}),
  };
};
